import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Options and OptionRow, the selectable row.
 *
 * Source selectors: `system.css` `.dsk-options`, `.dsk-option-row`,
 * `--compact`, `__body`, `__title`, `__sub`, `__end`.
 *
 * Doctrine enforced:
 * - The WHOLE ROW is the hit target, with the control inside it. The row is a
 *   `<label>`, so the input is associated by containment and no `htmlFor` can
 *   go stale. That is also how the 44px-plus target is met without inflating
 *   the 18px radio itself.
 * - Selection is STATE, so a chosen row is an accent tint plus an accent inset
 *   ring, never an ink fill. The CSS does that with
 *   `:has(.dsk-radio:checked)`, so it follows the real input; `selected` is the
 *   `.is-selected` fallback for a row that carries no input at all.
 * - The hover surface is mixed from `--text-primary`, not `--bg-secondary`,
 *   because in dark `--bg-secondary` equals `--bg-card` and a hover built on it
 *   vanishes the moment the row sits on a card.
 * - `:focus-within` puts the ring on the row, so keyboard focus on the small
 *   input is visible at row scale.
 *
 *   <Options>
 *     <OptionRow control={<Radio name="ship" value="std" />} title="Standard"
 *                sub="Nationwide" end="R 60" />
 *   </Options>
 */
export const Options = React.forwardRef(function Options({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('dsk-options', className)} {...rest} />;
});
Options.displayName = 'Options';

export const OptionRow = React.forwardRef(function OptionRow(
  { control, title, sub, end, compact = false, selected = false, children, className, ...rest },
  ref,
) {
  return (
    <label
      ref={ref}
      className={cn(
        'dsk-option-row',
        compact && 'dsk-option-row--compact',
        selected && 'is-selected',
        className,
      )}
      {...rest}
    >
      {control}
      {children || (
        <span className="dsk-option-row__body">
          {title ? <span className="dsk-option-row__title">{title}</span> : null}
          {sub ? <span className="dsk-option-row__sub">{sub}</span> : null}
        </span>
      )}
      {end !== undefined && end !== null ? (
        <span className="dsk-option-row__end">{end}</span>
      ) : null}
    </label>
  );
});
OptionRow.displayName = 'OptionRow';

export default OptionRow;
